/**
 * Mensagens de erro do cadastro de nova conta (#289)
 *
 * Regra: o cadastro nunca falha sem explicação. Toda falha vira um texto em
 * português que diz o que fazer; "Network Error", "Request failed with status code
 * 500" e textos técnicos nunca chegam à tela.
 *
 * @author Equipe VendeAi
 * @since 1.9.0
 */

import { friendlyNetworkError, API_TIMEOUT_MESSAGE } from '@utils/helpers';

export type RegisterErrorKind =
  | 'network' // sem resposta do servidor (internet, CORS, servidor fora)
  | 'timeout' // servidor demorou: a conta pode ter sido criada
  | 'email_in_use' // 409
  | 'validation' // 400 com erros por campo
  | 'rate_limit' // 429
  | 'closed' // 403: cadastros suspensos
  | 'server' // 5xx
  | 'other';

export interface RegisterErrorInfo {
  kind: RegisterErrorKind;
  message: string;
  /** Erros por campo vindos do servidor (campo do formulário → mensagem) */
  fieldErrors: Record<string, string>;
}

export const REGISTER_NETWORK_MESSAGE =
  'Não conseguimos falar com o servidor do VendeAi. Confira a sua internet e tente de novo. ' +
  'Se continuar, tente por outra rede (Wi-Fi ou dados móveis) ou fale com o nosso suporte.';

export const REGISTER_TIMEOUT_MESSAGE =
  'O servidor demorou para responder e não sabemos se a sua conta foi criada. ' +
  'Tente entrar com o e-mail e a senha que você digitou; se não der, tente o cadastro de novo.';

export const REGISTER_SERVER_MESSAGE =
  'Tivemos um problema do nosso lado ao criar a sua conta. Tente de novo em alguns minutos; ' +
  'se continuar, fale com o nosso suporte.';

export const REGISTER_EMAIL_IN_USE_MESSAGE =
  'Já existe uma conta com este e-mail. Entre com a sua senha ou use "Esqueci minha senha".';

export const REGISTER_RATE_LIMIT_MESSAGE =
  'Muitas tentativas de cadastro a partir desta conexão. Aguarde alguns minutos e tente de novo, ' +
  'ou use outra rede (por exemplo, os dados móveis em vez do Wi-Fi).';

export const REGISTER_GENERIC_MESSAGE =
  'Não foi possível criar a sua conta agora. Confira os dados e tente de novo; ' +
  'se continuar, fale com o nosso suporte.';

/** Texto que parece técnico (inglês do axios/servidor, stack, SQL) e não deve ir à tela */
function looksTechnical(message: string): boolean {
  return (
    /status code|network error|timeout of|econn|prisma|invalid `|at \w+ \(|stack|undefined|null|sql|internal server error/i.test(
      message,
    ) || message === 'Erro interno do servidor'
  );
}

interface AxiosLikeError {
  code?: string;
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
  };
}

/** Converte qualquer erro do cadastro em mensagem acionável em português */
export function describeRegisterError(error: unknown): RegisterErrorInfo {
  const e = (error ?? {}) as AxiosLikeError;
  const fieldErrors: Record<string, string> = {};

  // Sem resposta: falta de internet, origem recusada pelo CORS ou servidor fora do ar
  if (!e.response) {
    const friendly = friendlyNetworkError({ code: e.code, message: e.message });
    if (friendly === API_TIMEOUT_MESSAGE) {
      return { kind: 'timeout', message: REGISTER_TIMEOUT_MESSAGE, fieldErrors };
    }
    if (friendly || e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED') {
      return { kind: 'network', message: REGISTER_NETWORK_MESSAGE, fieldErrors };
    }
    return { kind: 'other', message: REGISTER_GENERIC_MESSAGE, fieldErrors };
  }

  const status = e.response.status ?? 0;
  const data = (e.response.data ?? {}) as {
    error?: {
      message?: unknown;
      code?: unknown;
      errors?: Array<{ field?: unknown; message?: unknown }>;
    };
  };
  const rawMessage = typeof data.error?.message === 'string' ? data.error.message.trim() : '';
  const serverMessage = rawMessage && !looksTechnical(rawMessage) ? rawMessage : '';
  const code = typeof data.error?.code === 'string' ? data.error.code : '';

  // Só o código do e-mail: outro 409 (ex.: outra unicidade no banco) não é "e-mail em uso"
  if (code === 'EMAIL_ALREADY_EXISTS') {
    return { kind: 'email_in_use', message: REGISTER_EMAIL_IN_USE_MESSAGE, fieldErrors };
  }

  if (status === 429) {
    return {
      kind: 'rate_limit',
      message: serverMessage || REGISTER_RATE_LIMIT_MESSAGE,
      fieldErrors,
    };
  }

  if (status === 400 || status === 422) {
    for (const item of data.error?.errors ?? []) {
      if (
        typeof item?.field === 'string' &&
        typeof item.message === 'string' &&
        !fieldErrors[item.field]
      ) {
        fieldErrors[item.field] = item.message;
      }
    }
    return {
      kind: 'validation',
      message: serverMessage || 'Confira os dados informados e tente de novo.',
      fieldErrors,
    };
  }

  if (status === 403 && code === 'REGISTRATIONS_DISABLED') {
    return { kind: 'closed', message: serverMessage || REGISTER_GENERIC_MESSAGE, fieldErrors };
  }

  if (status >= 500) {
    // 503 com mensagem nossa (ex.: endereço da loja ocupado) é acionável; o resto, texto padrão.
    // 502/504 do Nginx chegam em HTML, sem mensagem
    return {
      kind: 'server',
      message: status === 503 && serverMessage ? serverMessage : REGISTER_SERVER_MESSAGE,
      fieldErrors,
    };
  }

  return { kind: 'other', message: serverMessage || REGISTER_GENERIC_MESSAGE, fieldErrors };
}

/** Domínios de e-mail digitados errado com frequência → domínio certo */
const EMAIL_DOMAIN_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.com.br': 'gmail.com',
  'gmal.com': 'gmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outlook.con': 'outlook.com',
  'yahoo.com.b': 'yahoo.com.br',
  'yahoo.con.br': 'yahoo.com.br',
  'icloud.con': 'icloud.com',
};

/** Sugestão para e-mail com domínio digitado errado (null = sem sugestão) */
export function suggestEmailFix(email: string): string | null {
  const value = email.trim().toLowerCase();
  const at = value.lastIndexOf('@');
  if (at < 1) return null;
  const fixed = EMAIL_DOMAIN_TYPOS[value.slice(at + 1)];
  return fixed ? `${value.slice(0, at)}@${fixed}` : null;
}
